<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { Decimal } from 'decimal.js'
import { api, ApiError, money, yuan } from '../api.js'
import { displayNumber } from '../display-number.js'
import { db } from '../db.js'
import { inferPurchaseBasis, recalculatePurchase, type PurchaseCalculationBasis } from '../purchase-calculation.js'
import { onSync, persistAndQueue, persistDraft, queueDraft, resolveConflict } from '../sync.js'
import type { DayData, Item, RecordRow, SyncState } from '../types.js'
import SyncBadge from './SyncBadge.vue'

type Form = { expectedVersion: number; quantity: string; unitPrice: string; amount: string; actualBaseQuantity: string; note: string }
const todayShanghai = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Shanghai', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date())
const props = defineProps<{ initialDate?: string }>()
const selectedDate = ref(props.initialDate ?? todayShanghai())
const morningWeather = ref('—')
const morningWeatherHint = ref('早晨6–9点天气暂缺')
watch(selectedDate, async (date, _previous, onCleanup) => {
  let active = true
  onCleanup(() => { active = false })
  morningWeather.value = '—'
  morningWeatherHint.value = '正在获取早晨6–9点天气'
  try {
    const result = await api<{ days: Record<string, 'sunny' | 'rainy' | null>; configured: boolean; unavailable: boolean }>(`/api/weather?start=${date}&end=${date}`)
    if (!active) return
    const condition = result.days[date]
    morningWeather.value = condition === 'sunny' ? '晴' : condition === 'rainy' ? '雨' : '—'
    morningWeatherHint.value = condition ? `早晨6–9点：${condition === 'rainy' ? '有雨' : '无雨（含阴、多云）'}` : !result.configured ? '天气待设置店铺位置' : result.unavailable ? '天气获取失败' : '早晨6–9点天气暂缺'
  } catch {
    if (active) morningWeatherHint.value = '天气获取失败'
  }
}, { immediate: true })
const day = ref<DayData | null>(null)
const loading = ref(true)
const cached = ref(false)
const loadError = ref('')
const storageError = ref('')
const status = reactive<Record<string, SyncState>>({})
const forms = reactive<Record<string, Form>>({})
const expenseEdits = reactive<Record<string, { expectedVersion: number; name: string; category: string; unit: string; quantity: string; unitPrice: string; amount: string; note: string }>>({})
const timers = new Map<string, number>()
const showPurchasePicker = ref(false)
const showExpenseForm = ref(false)
const editingExpenseId = ref<string | null>(null)
const addedPurchaseIds = ref<string[]>([])
const hiddenBlankPurchaseIds = ref<string[]>([])
const purchaseBasis = reactive<Record<string, PurchaseCalculationBasis>>({})
const expenseForm = reactive({ itemId: '', name: '', amount: '', note: '' })
const manualPurchase = ref('')
const purchaseNote = ref('')
const purchaseVersion = ref(0)
const manualSales = ref('')
const salesNote = ref('')
const showSaleQuantities = ref(false)
const saleQuantityForms = reactive<Record<string, { quantity: string; expectedVersion: number }>>({})
const saleItems = computed(() => day.value?.items.filter(item => item.kind === 'sale' && item.enabled) ?? [])
const quantityKey = (itemId: string) => `sales-quantity:${selectedDate.value}:${itemId}`
const saleVersion = ref(0)
const conflicts = ref<Array<any>>([])
let stopSync: (() => void) | undefined
let approvedHistoricalField: HTMLElement | null = null
const historicalGuardHandlers = new WeakMap<HTMLElement, { pointerdown: (event: PointerEvent) => void; focus: () => void; blur: () => void }>()

const isHistoricalDate = () => selectedDate.value !== todayShanghai()
function confirmHistoricalEdit(action = '修改这一项') {
  if (!isHistoricalDate()) return true
  return window.confirm(`当前查看的是 ${displayDate.value}，不是今天的数据。\n确定要${action}吗？`)
}

const vHistoricalGuard = {
  mounted(element: HTMLElement) {
    const approve = () => {
      if (!isHistoricalDate() || approvedHistoricalField === element) return true
      if (!confirmHistoricalEdit()) return false
      approvedHistoricalField = element
      return true
    }
    const pointerdown = (event: PointerEvent) => {
      if (approve()) return
      event.preventDefault()
      event.stopPropagation()
    }
    const focus = () => {
      if (!approve()) queueMicrotask(() => element.blur())
    }
    const blur = () => {
      if (approvedHistoricalField === element) approvedHistoricalField = null
    }
    historicalGuardHandlers.set(element, { pointerdown, focus, blur })
    element.addEventListener('pointerdown', pointerdown, true)
    element.addEventListener('focus', focus)
    element.addEventListener('blur', blur)
  },
  unmounted(element: HTMLElement) {
    const handlers = historicalGuardHandlers.get(element)
    if (!handlers) return
    element.removeEventListener('pointerdown', handlers.pointerdown, true)
    element.removeEventListener('focus', handlers.focus)
    element.removeEventListener('blur', handlers.blur)
    historicalGuardHandlers.delete(element)
  },
}

const allPurchaseItems = computed(() => day.value?.items.filter((item) => item.kind === 'purchase' && item.expenseKind === 'daily' && (item.enabled || day.value?.purchases.some(row => row.itemId === item.id && !row.deletedAt))) ?? [])
const purchaseItems = computed(() => allPurchaseItems.value.filter((item) => {
  const records = day.value?.purchases.filter((row) => row.itemId === item.id) ?? []
  const active = records.some((row) => !row.deletedAt)
  const deleted = records.some((row) => row.deletedAt)
  return !hiddenBlankPurchaseIds.value.includes(item.id) && (active || addedPurchaseIds.value.includes(item.id) || (item.fixedDaily && !deleted))
}))
const extraPurchaseItems = computed(() => allPurchaseItems.value.filter((item) => !purchaseItems.value.some((shown) => shown.id === item.id)))
const incidentalItems = computed(() => day.value?.items.filter((item) => item.kind === 'purchase' && item.expenseKind === 'incidental' && item.enabled) ?? [])
const incidentals = computed(() => day.value?.incidentals.filter((row) => !row.deletedAt) ?? [])
const deletedRows = computed(() => [...(day.value?.purchases ?? []), ...(day.value?.incidentals ?? [])].filter((row) => row.deletedAt))

function emptyForm(item: Item, record?: RecordRow): Form {
  const compact = (value: string | null | undefined) => value ? new Decimal(value).toString() : ''
  return record ? { expectedVersion: record.version, quantity: new Decimal(record.quantity).eq(0) ? '' : compact(record.quantity), unitPrice: yuan(record.unitPriceFen), amount: yuan(record.amountFen), actualBaseQuantity: compact(record.actualBaseQuantity), note: record.note }
    : { expectedVersion: 0, quantity: '', unitPrice: yuan(item.lastEffectivePriceFen ?? item.defaultPriceFen), amount: '', actualBaseQuantity: '', note: '' }
}

function editorKey(kind: 'purchase', itemId: string) { return `${kind}:${selectedDate.value}:${itemId}` }
function initForms(data: DayData) {
  for (const item of data.items.filter(item => item.kind === 'sale')) {
    const previous = data.salesQuantities?.find(row => row.itemId === item.id)
    saleQuantityForms[quantityKey(item.id)] = { quantity: new Decimal(previous?.quantity ?? 0).toString(), expectedVersion: previous?.businessDate === data.date ? previous.version : 0 }
  }
  for (const item of data.items) {
    if (item.kind === 'purchase' && item.expenseKind === 'daily') {
      const record = data.purchases.find((row) => row.itemId === item.id)
      const form = emptyForm(item, record && !record.deletedAt ? record : undefined)
      if (record?.deletedAt) form.expectedVersion = record.version
      forms[editorKey('purchase', item.id)] = form
      purchaseBasis[item.id] = inferPurchaseBasis(form)
    }
  }
  manualPurchase.value = data.purchaseDay?.manualTotalFen == null ? '' : yuan(data.purchaseDay.manualTotalFen)
  purchaseNote.value = data.purchaseDay?.note ?? ''
  purchaseVersion.value = data.purchaseDay?.version ?? 0
  manualSales.value = data.salesDay.manualTotalFen === null ? '' : yuan(data.salesDay.manualTotalFen)
  salesNote.value = data.salesDay.note
  saleVersion.value = data.salesDay.version
  for (const row of data.incidentals.filter((entry) => !entry.deletedAt)) expenseEdits[row.id] = { expectedVersion: row.version, name: row.nameSnapshot, category: row.categorySnapshot, unit: row.unitSnapshot, quantity: new Decimal(row.quantity).eq(0) ? '' : new Decimal(row.quantity).toString(), unitPrice: yuan(row.unitPriceFen), amount: yuan(row.amountFen), note: row.note }
}

async function applyDrafts(date: string) {
  const drafts = await db.drafts.where('date').equals(date).toArray()
  hiddenBlankPurchaseIds.value = []
  for (const draft of drafts) {
    if (draft.entityKey.startsWith('sales-quantity:') && saleQuantityForms[draft.entityKey]) Object.assign(saleQuantityForms[draft.entityKey], draft.payload)
    if (draft.entityKey.startsWith('purchase:')) {
      if (draft.payload.deleted) {
        hiddenBlankPurchaseIds.value.push(draft.entityKey.split(':').at(-1) ?? '')
        continue
      }
      Object.assign(forms[draft.entityKey], draft.payload)
      const itemId = draft.entityKey.split(':').at(-1) ?? ''
      if (itemId && !addedPurchaseIds.value.includes(itemId)) addedPurchaseIds.value.push(itemId)
      purchaseBasis[itemId] = inferPurchaseBasis(forms[draft.entityKey])
    }
    if (draft.entityKey.startsWith('incidental:')) {
      const edit = expenseEdits[draft.entityKey.split(':')[1]]
      if (edit) Object.assign(edit, draft.payload)
    }
    if (draft.entityKey === `purchase-total:${date}`) { manualPurchase.value = String(draft.payload.manualTotal ?? ''); purchaseNote.value = String(draft.payload.note ?? '') }
    if (draft.entityKey === `sales-total:${date}`) { manualSales.value = String(draft.payload.manualTotal ?? ''); salesNote.value = String(draft.payload.note ?? '') }
  }
}

async function load(preferCache = true) {
  loading.value = true
  loadError.value = ''
  const date = selectedDate.value
  if (preferCache) {
    const local = await db.days.get(date)
    if (local) { day.value = local.data; cached.value = true; initForms(local.data); await applyDrafts(date); loading.value = false }
  }
  try {
    const fresh = await api<DayData>(`/api/day?date=${date}`)
    if (date !== selectedDate.value) return
    day.value = fresh
    cached.value = false
    initForms(fresh)
    await applyDrafts(date)
    try { await db.days.put({ date, data: fresh, fetchedAt: Date.now() }) } catch { storageError.value = '本地存储写入失败；当前输入无法保证离线防丢。' }
  } catch (error) {
    if (!day.value || day.value.date !== date) loadError.value = (error as ApiError).message
  } finally { loading.value = false }
}

function calculatedAmount(form: Form) {
  if (form.amount !== '') return form.amount
  if (!form.quantity || !form.unitPrice) return '0'
  try { return new Decimal(form.quantity).mul(form.unitPrice).toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toFixed(2) } catch { return '0' }
}

function converted(item: Item, form: Form) {
  if (form.actualBaseQuantity) return form.actualBaseQuantity
  if (!item.conversionFactor || !form.quantity) return ''
  try { return new Decimal(form.quantity).mul(item.conversionFactor).toDecimalPlaces(4).toString() } catch { return '' }
}

function convertedCost(item: Item, form: Form) {
  const baseQuantity = form.actualBaseQuantity || converted(item, form)
  const amount = calculatedAmount(form)
  if (!baseQuantity || !amount || !item.baseUnit) return ''
  try {
    const quantity = new Decimal(baseQuantity)
    if (!quantity.gt(0)) return ''
    return new Decimal(amount).div(quantity).toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toFixed(2)
  } catch { return '' }
}

function purchaseInput(item: Item, changed: 'quantity' | 'unitPrice' | 'amount') {
  const form = forms[editorKey('purchase', item.id)]
  purchaseBasis[item.id] = recalculatePurchase(form, changed, purchaseBasis[item.id] ?? 'unitPrice')
  schedule('purchase', item)
}

function addPurchaseItem(itemId: string) {
  if (!itemId || addedPurchaseIds.value.includes(itemId)) return
  addedPurchaseIds.value.push(itemId)
  hiddenBlankPurchaseIds.value = hiddenBlankPurchaseIds.value.filter((id) => id !== itemId)
  showPurchasePicker.value = false
}

async function removePurchaseItem(item: Item, confirmed = false) {
  if (!confirmed && !confirmHistoricalEdit(`删除“${item.name}”`)) return
  const key = editorKey('purchase', item.id)
  window.clearTimeout(timers.get(key))
  if (!hiddenBlankPurchaseIds.value.includes(item.id)) hiddenBlankPurchaseIds.value.push(item.id)
  addedPurchaseIds.value = addedPurchaseIds.value.filter((id) => id !== item.id)
  try {
    await persistAndQueue(key, selectedDate.value, 'PUT', `/api/daily-purchases/${selectedDate.value}/${item.id}`, { ...forms[key], deleted: true })
  } catch { storageError.value = '本地存储写入失败；当天移除操作尚未保存。' }
}

async function replacePurchaseItem(current: Item, nextId: string) {
  if (!nextId || nextId === current.id) return
  await removePurchaseItem(current, true)
  addPurchaseItem(nextId)
}

function schedule(kind: 'purchase', item: Item) {
  const key = editorKey(kind, item.id)
  status[key] = 'local'
  const currentPayload = { ...forms[key] }
  void persistDraft(key, selectedDate.value, currentPayload).catch(() => { storageError.value = '本地存储写入失败；这次输入尚未安全保存。'; status[key] = 'failed' })
  window.clearTimeout(timers.get(key))
  timers.set(key, window.setTimeout(async () => {
    try {
      const payload = { ...forms[key] }
      await queueDraft(key, selectedDate.value, 'PUT', `/api/daily-purchases/${selectedDate.value}/${item.id}`, payload)
    } catch { storageError.value = '本地存储写入失败；这次输入尚未安全保存。'; status[key] = 'failed' }
  }, 450))
}

function scheduleSalesTotal() {
  const key = `sales-total:${selectedDate.value}`
  status[key] = 'local'
  const currentPayload = { expectedVersion: saleVersion.value, manualTotal: manualSales.value, note: salesNote.value }
  void persistDraft(key, selectedDate.value, currentPayload).catch(() => { storageError.value = '本地存储写入失败；销售总额尚未安全保存。'; status[key] = 'failed' })
  window.clearTimeout(timers.get(key))
  timers.set(key, window.setTimeout(async () => {
    try { await queueDraft(key, selectedDate.value, 'PUT', `/api/sales-total/${selectedDate.value}`, { expectedVersion: saleVersion.value, manualTotal: manualSales.value, note: salesNote.value }) }
    catch { storageError.value = '本地存储写入失败；销售总额尚未安全保存。'; status[key] = 'failed' }
  }, 450))
}

function scheduleSaleQuantity(item: Item) {
  const date = selectedDate.value
  const key = quantityKey(item.id)
  const payload = { ...saleQuantityForms[key] }
  status[key] = 'local'
  const failed = () => { storageError.value = '本地存储写入失败；商品数量尚未安全保存。'; status[key] = 'failed' }
  void persistDraft(key, date, payload).catch(failed)
  window.clearTimeout(timers.get(key))
  timers.set(key, window.setTimeout(() => {
    void queueDraft(key, date, 'PUT', `/api/sales-quantities/${date}/${item.id}`, payload).catch(failed)
  }, 450))
}

function schedulePurchaseTotal() {
  const key = `purchase-total:${selectedDate.value}`
  status[key] = 'local'
  const currentPayload = { expectedVersion: purchaseVersion.value, manualTotal: manualPurchase.value, note: purchaseNote.value }
  void persistDraft(key, selectedDate.value, currentPayload).catch(() => { storageError.value = '本地存储写入失败；采购总额尚未安全保存。'; status[key] = 'failed' })
  window.clearTimeout(timers.get(key))
  timers.set(key, window.setTimeout(async () => {
    try { await queueDraft(key, selectedDate.value, 'PUT', `/api/purchase-total/${selectedDate.value}`, { expectedVersion: purchaseVersion.value, manualTotal: manualPurchase.value, note: purchaseNote.value }) }
    catch { storageError.value = '本地存储写入失败；采购总额尚未安全保存。'; status[key] = 'failed' }
  }, 450))
}

function scheduleExpense(row: RecordRow) {
  const key = `incidental:${row.id}`
  const payload = { ...expenseEdits[row.id] }
  status[key] = 'local'
  void persistDraft(key, selectedDate.value, payload).catch(() => { storageError.value = '本地存储写入失败；这笔支出尚未安全保存。'; status[key] = 'failed' })
  window.clearTimeout(timers.get(key))
  timers.set(key, window.setTimeout(() => void queueDraft(key, selectedDate.value, 'PATCH', `/api/incidental-expenses/${row.id}`, { ...expenseEdits[row.id] }).catch(() => { storageError.value = '本地存储写入失败；这笔支出尚未安全保存。' }), 450))
}

async function addExpense() {
  const selectedItem = incidentalItems.value.find((item) => item.id === expenseForm.itemId)
  const name = selectedItem?.name ?? expenseForm.name.trim()
  if (!name || expenseForm.amount === '') return
  const key = `incidental-new:${selectedDate.value}:${crypto.randomUUID()}`
  try {
    await persistAndQueue(key, selectedDate.value, 'POST', '/api/incidental-expenses', { date: selectedDate.value, itemId: selectedItem?.id ?? null, name, category: selectedItem?.category ?? '', unit: selectedItem?.unit ?? '', quantity: '', unitPrice: '', amount: expenseForm.amount, note: expenseForm.note })
    Object.assign(expenseForm, { itemId: '', name: '', amount: '', note: '' })
    showExpenseForm.value = false
  } catch { storageError.value = '本地存储写入失败；这笔支出尚未安全保存。' }
}

async function deleteExpense(row: RecordRow) {
  if (!confirmHistoricalEdit(`删除“${expenseEdits[row.id].name}”`)) return
  row.deletedAt = 'pending'
  editingExpenseId.value = null
  await changeDeleted('incidental', row, 'delete', true)
}

function expenseItemChanged() {
  const item = incidentalItems.value.find((entry) => entry.id === expenseForm.itemId)
  expenseForm.name = ''
  expenseForm.amount = item?.defaultPriceFen ? yuan(item.defaultPriceFen) : ''
}

async function changeDeleted(type: 'purchase' | 'incidental', row: RecordRow, action: 'delete' | 'restore', confirmed = false) {
  if (!confirmed && !confirmHistoricalEdit(`${action === 'delete' ? '删除' : '恢复'}“${row.nameSnapshot}”`)) return
  const key = `${type}-${action}:${row.id}`
  try { await persistAndQueue(key, selectedDate.value, 'POST', `/api/records/${type}/${row.id}/${action}`, { expectedVersion: row.version }) }
  catch { storageError.value = '本地存储写入失败；操作尚未保存。' }
}

const localSummary = computed(() => {
  if (!day.value) return null
  const cents = (value: string) => { try { return new Decimal(value || 0).mul(100).toDecimalPlaces(0, Decimal.ROUND_HALF_UP) } catch { return new Decimal(0) } }
  const sales = cents(manualSales.value)
  const detailPurchase = purchaseItems.value.reduce((sum, item) => sum.add(cents(calculatedAmount(forms[editorKey('purchase', item.id)]))), new Decimal(0))
  const purchase = manualPurchase.value === '' ? detailPurchase : cents(manualPurchase.value)
  const incidental = incidentals.value.reduce((sum, row) => sum.add(cents(expenseEdits[row.id]?.amount ?? yuan(row.amountFen))), new Decimal(0))
  return { sales: sales.toFixed(0), purchase: purchase.toFixed(0), incidental: incidental.toFixed(0), expense: purchase.add(incidental).toFixed(0), balance: sales.sub(purchase).sub(incidental).toFixed(0), detailPurchase: detailPurchase.toFixed(0) }
})

const purchaseDifference = computed(() => manualPurchase.value === '' || !localSummary.value ? null : new Decimal(localSummary.value.purchase).sub(localSummary.value.detailPurchase).div(100).toFixed(2))

const displayDate = computed(() => {
  const [, month, date] = selectedDate.value.split('-').map(Number)
  return `${month}月${date}日`
})
const compactMoney = (fen: string) => {
  const value = new Decimal(fen || 0).div(100)
  return `¥${value.isInteger() ? value.toFixed(0) : value.toFixed(2)}`
}

async function refreshConflicts() { conflicts.value = await db.operations.where('state').equals('conflict').toArray() }
async function chooseConflict(conflict: any, choice: 'server' | 'local') { await resolveConflict(conflict.operationId, choice, Number(conflict.serverValue?.version ?? 0)); await refreshConflicts(); if (choice === 'server') await load(false) }

watch(selectedDate, async () => { approvedHistoricalField = null; day.value = null; addedPurchaseIds.value = []; hiddenBlankPurchaseIds.value = []; showPurchasePicker.value = false; editingExpenseId.value = null; await nextTick(); await load() })
onMounted(() => {
  void load()
  stopSync = onSync((event) => { status[event.entityKey] = event.state as SyncState; if (event.state === 'synced') window.setTimeout(() => void load(false), 100); if (event.state === 'conflict') void refreshConflicts() })
  addEventListener('zaocan-remote-change', remoteChanged)
  void refreshConflicts()
})
function remoteChanged() { void load(false) }
onBeforeUnmount(() => { stopSync?.(); removeEventListener('zaocan-remote-change', remoteChanged); timers.forEach((timer) => clearTimeout(timer)) })
</script>

<template>
  <main class="page today-page">
    <header class="compact-date-header">
      <div class="date-with-weather"><label class="date-tap"><span>{{ displayDate }}</span><input v-model="selectedDate" type="date" aria-label="业务日期" /></label><span class="date-weather" role="status" :title="morningWeatherHint" :aria-label="morningWeatherHint">{{ morningWeather }}</span></div>
      <h1>{{ selectedDate === todayShanghai() ? '今天' : '补记' }}</h1>
    </header>

    <p v-if="isHistoricalDate()" class="historical-warning" role="status">正在查看 {{ displayDate }} 的历史账目，修改每一项前都会再次确认。</p>

    <p v-if="cached" class="cache-note">当前显示本地缓存，正在获取服务器最新账本…</p>
    <p v-if="loadError" class="form-error">{{ loadError }}（未加载的数据不会按 0 计算）</p>
    <p v-if="storageError" class="storage-error">{{ storageError }}</p>
    <div v-if="loading && !day" class="skeleton-card">正在加载这一天的账目…</div>

    <template v-if="day && localSummary">
      <section class="daily-sheet compact-ledger-section">
        <div class="compact-section-heading">
          <h2>今日采购</h2>
          <label class="compact-total-edit" :class="{ overridden: manualPurchase !== '' }"><span>¥</span><input v-replace-number="manualPurchase" v-historical-guard v-model="manualPurchase" aria-label="今日采购总额" inputmode="decimal" :placeholder="yuan(localSummary.detailPurchase)" @input="schedulePurchaseTotal" /></label>
        </div>
        <div class="purchase-grid-head" aria-hidden="true"><span>食材</span><span>数量</span><span>单价</span><span>总价</span><span></span></div>
        <article v-for="item in purchaseItems" :key="item.id" class="purchase-table-row">
          <label class="purchase-select-cell"><select v-historical-guard :value="item.id" :aria-label="`${item.name}食材`" @change="replacePurchaseItem(item, ($event.target as HTMLSelectElement).value)">
            <option v-for="candidate in allPurchaseItems" :key="candidate.id" :value="candidate.id" :disabled="purchaseItems.some((shown) => shown.id === candidate.id && shown.id !== item.id)">{{ candidate.name }}</option>
          </select><span aria-hidden="true">⌄</span></label>
          <label class="inline-unit-input"><input v-replace-number="forms[editorKey('purchase', item.id)].quantity" v-historical-guard v-model="forms[editorKey('purchase', item.id)].quantity" inputmode="decimal" :aria-label="`${item.name}数量`" placeholder="0" @input="purchaseInput(item, 'quantity')" /><span>{{ item.unit }}</span></label>
          <input v-replace-number="forms[editorKey('purchase', item.id)].unitPrice" v-historical-guard v-model="forms[editorKey('purchase', item.id)].unitPrice" class="table-number-input" inputmode="decimal" :aria-label="`${item.name}单价`" placeholder="0.00" @input="purchaseInput(item, 'unitPrice')" />
          <input v-replace-number="forms[editorKey('purchase', item.id)].amount" v-historical-guard v-model="forms[editorKey('purchase', item.id)].amount" class="table-number-input" inputmode="decimal" :aria-label="`${item.name}总价`" placeholder="0.00" @input="purchaseInput(item, 'amount')" />
          <button class="line-delete" :aria-label="`删除${item.name}`" @click="removePurchaseItem(item)">×</button>
          <small v-if="item.baseUnit && converted(item, forms[editorKey('purchase', item.id)])" class="conversion-note">折合 {{ displayNumber(converted(item, forms[editorKey('purchase', item.id)])) }}{{ item.baseUnit }}<template v-if="convertedCost(item, forms[editorKey('purchase', item.id)])"> · ¥{{ convertedCost(item, forms[editorKey('purchase', item.id)]) }}/{{ item.baseUnit }}</template></small>
          <SyncBadge :state="status[editorKey('purchase', item.id)]" />
        </article>
        <div v-if="showPurchasePicker" class="purchase-add-row">
          <select v-historical-guard aria-label="选择要添加的食材" :value="''" @change="addPurchaseItem(($event.target as HTMLSelectElement).value)"><option value="" disabled>选择食材</option><option v-for="item in extraPurchaseItems" :key="item.id" :value="item.id">{{ item.name }} / {{ item.unit || '无单位' }}</option></select>
          <span v-if="!extraPurchaseItems.length">请先到“我的”新增采购项目</span>
        </div>
        <button class="add-line-button" @click="showPurchasePicker = !showPurchasePicker">＋ 添加一行</button>
      </section>

      <section class="daily-sheet expense-section">
        <div class="compact-section-heading"><h2>其他支出</h2><strong>{{ compactMoney(localSummary.incidental) }}</strong></div>
        <article v-for="row in incidentals" :key="row.id" class="expense-list-row">
          <template v-if="editingExpenseId !== row.id">
            <button class="expense-row-main" @click="editingExpenseId = row.id"><span>{{ expenseEdits[row.id].name }}</span><strong>¥{{ expenseEdits[row.id].amount || '0' }}</strong></button>
            <button class="line-delete" :aria-label="`删除${expenseEdits[row.id].name}`" @click="deleteExpense(row)">×</button>
          </template>
          <div v-else class="expense-inline-editor">
            <input v-historical-guard v-model="expenseEdits[row.id].name" aria-label="支出名称" @input="scheduleExpense(row)" />
            <input v-replace-number="expenseEdits[row.id].amount" v-historical-guard v-model="expenseEdits[row.id].amount" inputmode="decimal" aria-label="支出金额" @input="scheduleExpense(row)" />
            <input v-historical-guard v-model="expenseEdits[row.id].note" aria-label="支出备注" placeholder="备注（可选）" @input="scheduleExpense(row)" />
            <button @click="editingExpenseId = null">完成</button>
          </div>
          <SyncBadge :state="status[`incidental:${row.id}`]" />
        </article>
        <form v-if="showExpenseForm" class="expense-add-form" @submit.prevent="addExpense">
          <select v-if="incidentalItems.length" v-historical-guard v-model="expenseForm.itemId" aria-label="支出项目" @change="expenseItemChanged"><option value="">临时支出</option><option v-for="item in incidentalItems" :key="item.id" :value="item.id">{{ item.name }}</option></select>
          <input v-if="!expenseForm.itemId" v-historical-guard v-model="expenseForm.name" aria-label="临时支出名称" placeholder="支出名称" required />
          <input v-replace-number="expenseForm.amount" v-historical-guard v-model="expenseForm.amount" aria-label="支出金额" inputmode="decimal" placeholder="金额" required />
          <input v-historical-guard v-model="expenseForm.note" class="expense-note" aria-label="支出备注" placeholder="备注（可选）" />
          <button>保存</button>
        </form>
        <button class="add-line-button" @click="showExpenseForm = !showExpenseForm">{{ showExpenseForm ? '取消' : '＋ 添加支出' }}</button>
      </section>

      <section class="daily-sheet sales-section">
        <div class="sales-main"><h2>今日售卖总额</h2><label class="sales-money"><span>¥</span><input v-replace-number="manualSales" v-historical-guard v-model="manualSales" aria-label="今日销售总额" inputmode="decimal" placeholder="0.00" @input="scheduleSalesTotal" /></label><SyncBadge :state="status[`sales-total:${selectedDate}`]" /></div>
        <button class="sale-quantity-toggle" :aria-expanded="showSaleQuantities" @click="showSaleQuantities = !showSaleQuantities">商品数量 <span>{{ showSaleQuantities ? '收起' : '展开' }} {{ saleItems.length }}项</span></button>
        <div v-if="showSaleQuantities">
          <p class="hint">沿用上次数量，可直接修改；数量不参与收入计算。</p>
          <label v-for="item in saleItems" :key="item.id" class="sale-quantity-row"><span>{{ item.name }}</span><input v-replace-number="saleQuantityForms[quantityKey(item.id)].quantity" v-historical-guard v-model="saleQuantityForms[quantityKey(item.id)].quantity" :aria-label="`${item.name}售卖数量`" inputmode="decimal" @input="scheduleSaleQuantity(item)" /><span>{{ item.unit }}</span><SyncBadge :state="status[quantityKey(item.id)]" /></label>
          <p v-if="!saleItems.length" class="hint">在“我的 → 售卖商品”中添加商品。</p>
        </div>
      </section>

      <section v-if="deletedRows.length" class="undo-section"><h2>最近删除</h2><div v-for="row in deletedRows" :key="row.id" class="compact-row"><span>{{ row.nameSnapshot }} · {{ money(row.amountFen) }}</span><button class="text-button" @click="changeDeleted(day.purchases.includes(row) ? 'purchase' : 'incidental', row, 'restore')">恢复</button></div></section>
      <section v-if="conflicts.length" class="conflict-box"><h2>需要选择保留哪个版本</h2><article v-for="conflict in conflicts" :key="conflict.operationId"><p>{{ conflict.error }}</p><div><button @click="chooseConflict(conflict, 'server')">使用服务器版本</button><button class="primary-button" @click="chooseConflict(conflict, 'local')">保留我的输入</button></div></article></section>

      <aside class="day-summary-strip"><span>支出 <b>{{ compactMoney(localSummary.expense) }}</b></span><span>收入 <b>{{ compactMoney(localSummary.sales) }}</b></span><span :class="{ negative: Number(localSummary.balance) < 0 }">结余 <b>{{ compactMoney(localSummary.balance) }}</b></span></aside>
    </template>
  </main>
</template>
